/**
 * This is the launcher for the Create Assignment modal so
 * users can assign content.
 * Actual UI to show the modal should be defined upstream.
 **/

import * as React from "react";
import {useDispatch, useSelector} from "react-redux";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {
    checkHasGroupedExercises,
    fetchClassLists,
    newAssignment,
    stopEditingAssignment,
    updateEditedAssignment,
} from "@khan/assignments-store";
import {
    type Assignment,
    type State as AssignmentStoreState,
    type DCULPath,
    type QuestionSetTypeEnum,
    type Student,
} from "@khan/assignments-types";
import {getUserHasChildAssignmentsClassroomStatus} from "@khan/assignments-utils";
// eslint-disable-next-line no-restricted-imports -- TODO(FEI-7051): switch to useGql
import {useApolloGqlClient} from "@khan/data-gql-apollo";

import AssignmentModal from "./assignment-modal.tsx";
import {getQualifiedDescriptor} from "./utils.tsx";

type AssignmentUpdateProps =
    | {
          dueDate: Date | null | undefined;
      }
    | {
          startDate: Date | null | undefined;
      }
    | {
          students: Array<Student>;
      }
    | {
          questionSetType: QuestionSetTypeEnum;
      };

interface Props {
    contentDescriptor: string;
    contentPath?: DCULPath;
    initialAssignment?: Assignment;
    subjectSlug: string;
    onClose?: () => unknown;
    onSubmit?: () => unknown;
    onSuccess?: () => unknown;
}

function AssignmentModalLauncher(props: Props): React.ReactNode {
    const {
        subjectSlug,
        onClose,
        onSubmit,
        onSuccess,
        contentDescriptor,
        contentPath,
        initialAssignment,
    } = props;

    const {gqlClient} = useApolloGqlClient();

    // Redux state
    const assignment = useSelector(
        (state: AssignmentStoreState) => state.editingAssignment.assignment,
    );
    const qualifiedContentDescriptorsList = useSelector(
        (state: AssignmentStoreState) =>
            state.editingAssignment.qualifiedContentDescriptorsList,
    );
    const hasGroupedExercises = useSelector(
        (state: AssignmentStoreState) => state.hasGroupedExercises,
    );
    const studentLists = useSelector(
        (state: AssignmentStoreState) => state.studentLists,
    );

    // Redux dispatch
    const dispatch = useDispatch();
    const onAssignmentUpdate = React.useCallback(
        (props: AssignmentUpdateProps) =>
            dispatch(updateEditedAssignment(props)),
        [dispatch],
    );

    const [modalIsOpen, setModalIsOpen] = React.useState(true);

    const initiateNewAssignment = React.useCallback((): void => {
        const descriptor = getQualifiedDescriptor(
            contentDescriptor,
            contentPath,
        );
        const contentDescriptors = [descriptor];

        dispatch(
            newAssignment({
                assignment: {
                    ...initialAssignment,
                    qualifiedContentDescriptors: contentDescriptors,
                },
                persistSettings: true,
            }),
        );
    }, [dispatch, contentDescriptor, contentPath, initialAssignment]);

    // Handle component mount logic
    useOnMountEffect(() => {
        if (contentDescriptor) {
            initiateNewAssignment();
        }

        // We need to fetch class lists here so that we have a list of all the
        // students in each class for assigning content
        fetchClassLists(gqlClient)(dispatch);
        checkHasGroupedExercises(gqlClient, [contentDescriptor])(dispatch);
    });

    // Handle content descriptor changes
    React.useEffect(() => {
        if (contentDescriptor) {
            initiateNewAssignment();
            checkHasGroupedExercises(gqlClient, [contentDescriptor])(dispatch);
        }
    }, [contentDescriptor, initiateNewAssignment, gqlClient, dispatch]);

    // Handle component unmount logic
    React.useEffect(() => {
        return () => {
            dispatch(stopEditingAssignment());
        };
    }, [dispatch]);

    if (!assignment) {
        return null;
    }

    const successModal = () => {
        onSuccess?.();
    };

    const closeModal = () => {
        setModalIsOpen(false);
        onClose?.();
    };

    const userHasCACs = getUserHasChildAssignmentsClassroomStatus(studentLists);

    return (
        <ModalLauncher
            opened={modalIsOpen}
            onClose={closeModal}
            modal={() => (
                <AssignmentModal
                    assignment={assignment}
                    qualifiedContentDescriptorsList={
                        qualifiedContentDescriptorsList
                    }
                    onAssignmentUpdate={onAssignmentUpdate}
                    onClose={() => closeModal()}
                    onMutationSuccess={() => successModal()}
                    subjectSlug={subjectSlug}
                    hasGroupedExercises={hasGroupedExercises}
                    onMutationSubmit={onSubmit}
                    hasChildAssignmentsClassroom={userHasCACs}
                />
            )}
        />
    );
}

export default AssignmentModalLauncher;
